import { useCallback, useEffect, useState } from "react";
import { listRuns, getRun, createRun } from "@services/agentSim";
import { fetchAdminClinics } from "@services/clinicUsers";

export function useAgentSimRuns() {
  const [clinics, setClinics] = useState([]);
  const [runs, setRuns] = useState([]);
  const [filters, setFilters] = useState({ clinicId: "", status: "", onlyFailed: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const clearError = () => setError(null);
  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  const refresh = useCallback(async (override) => {
    setLoading(true);
    try {
      const f = override || filters;
      const res = await listRuns(f);
      setRuns(res?.data || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchAdminClinics();
        setClinics(c?.data || []);
      } catch (e) { setError(e.message); }
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasPending = runs.some((r) => r.status === "queued" || r.status === "running");
    if (!hasPending) return undefined;
    const id = setInterval(() => { refresh(); }, 10000);
    return () => clearInterval(id);
  }, [runs, refresh]);

  const openRun = useCallback(async (run) => {
    setSelectedRun(run);
    setDetailLoading(true);
    try {
      const res = await getRun(run.id);
      setDetail(res?.data || null);
    } catch (e) { setError(e.message); } finally { setDetailLoading(false); }
  }, []);

  const closeRun = () => { setSelectedRun(null); setDetail(null); };

  const triggerRun = useCallback(async ({ clinicId, purpose }) => {
    try {
      await createRun({ clinicId, purpose, mode: "live" });
      await refresh();
    } catch (e) { setError(e.message); }
  }, [refresh]);

  return {
    clinics, runs, loading, error, clearError,
    filters, setFilter, refresh,
    selectedRun, openRun, closeRun, detail, detailLoading,
    triggerRun,
  };
}
